// WASettingsOverview.jsx — جولة 1 · جرد إعدادات واتساب (عرض فقط · لا كتابة).
//
// يقرأ GET /api/whatsapp/settings-overview فقط. لا api.put/post إطلاقًا.
// كل إعداد «حدود وسقوف» يحمل وسم مساره + جملة تحذير النظامين المنفصلين.
// المفاتيح بلا واجهة تُوسَم صراحةً؛ test_whitelist_phones مُبرَز كخطر.
// مفاتيح Meta تُعرَض كحالة (مضبوط/غير مضبوط) فقط — بلا أي قيمة.
import React, { useEffect, useState } from "react";
import { api } from "./lib/api";

const CARD = { background: "#FFFFFF", border: "1px solid rgba(170,149,102,0.35)", borderRadius: 12, padding: 14, marginBottom: 12 };
const MUTED = "#78716C";

function PathTag({ appliesTo, labels }) {
  if (!appliesTo) return null;
  const tone = appliesTo === "expected" ? "#0F766E" : appliesTo === "campaigns" ? "#B45309" : "#1D4ED8";
  const bg = appliesTo === "expected" ? "rgba(15,118,110,0.12)" : appliesTo === "campaigns" ? "rgba(180,83,9,0.12)" : "rgba(29,78,216,0.12)";
  return (
    <span data-testid={`wa-so-pathtag-${appliesTo}`}
      style={{ padding: "2px 10px", borderRadius: 999, background: bg, color: tone, fontSize: 11, fontWeight: 800, whiteSpace: "nowrap" }}>
      {labels[appliesTo] || appliesTo}
    </span>
  );
}

function fmtVal(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v, null, 0);
  return String(v);
}

export default function WASettingsOverview() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/whatsapp/settings-overview")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "تعذّر الجلب"));
  }, []);

  if (err) return <div data-testid="wa-so-error" style={{ ...CARD, color: "#B91C1C" }}>⚠ {err}</div>;
  if (!data) return <div data-testid="wa-so-loading" style={{ ...CARD, textAlign: "center", color: MUTED }}>⏳ جارٍ التحميل…</div>;

  const labels = data.path_labels || {};

  return (
    <div dir="rtl" data-testid="wa-settings-overview">
      <div style={{ ...CARD, background: "#FBF7F1" }}>
        <div style={{ fontWeight: 800, fontSize: 15, color: "#3D2A1C" }}>🗂 جرد إعدادات واتساب — عرض فقط</div>
        <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
          صفحة قراءة فقط — لا تعدّل أي قيمة. كل إعداد بمصدره وشاشته وصلاحيته والمسار الذي يؤثر فيه.
        </div>
      </div>

      {(data.categories || []).map((cat) => (
        <div key={cat.id} style={CARD} data-testid={`wa-so-cat-${cat.id}`}>
          <div style={{ fontWeight: 800, fontSize: 14, color: "#3D2A1C", marginBottom: 8 }}>{cat.label}</div>

          {cat.warning && (
            <div data-testid="wa-so-limits-warning"
              style={{ padding: 10, borderRadius: 8, background: "rgba(180,83,9,0.10)", border: "1px solid rgba(180,83,9,0.3)", color: "#92400E", fontSize: 12, lineHeight: 1.8, marginBottom: 10 }}>
              {cat.warning}
            </div>
          )}

          <div style={{ display: "grid", gap: 8 }}>
            {cat.items.map((it) => (
              <div key={it.key} data-testid={`wa-so-item-${it.key}`}
                style={{ padding: 10, borderRadius: 8,
                         background: it.danger ? "rgba(185,28,28,0.06)" : "rgba(61,42,28,0.04)",
                         border: it.danger ? "1px solid rgba(185,28,28,0.35)" : "1px solid rgba(61,42,28,0.08)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <b style={{ fontSize: 13, color: it.danger ? "#B91C1C" : "#3D2A1C" }}>{it.name_ar}</b>
                  <code style={{ fontSize: 11, direction: "ltr", color: MUTED }}>{it.key}</code>
                  {it.applies_to && <PathTag appliesTo={it.applies_to} labels={labels} />}
                  {it.no_ui && (
                    <span data-testid={`wa-so-noui-${it.key}`}
                      style={{ padding: "2px 9px", borderRadius: 999, background: "rgba(120,113,108,0.15)", color: "#57534E", fontSize: 10.5, fontWeight: 700 }}>
                      🚫 بلا واجهة — يُضبط من القاعدة فقط
                    </span>
                  )}
                  {it.danger && (
                    <span style={{ padding: "2px 9px", borderRadius: 999, background: "rgba(185,28,28,0.15)", color: "#B91C1C", fontSize: 10.5, fontWeight: 800 }}>
                      ⚠️ يتجاوز بوابة الجمهور
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: MUTED, marginTop: 5, lineHeight: 1.8 }}>
                  المصدر: <b>{it.source}</b> · الشاشة: <b>{it.screen}</b> · الصلاحية: <b>{it.permission}</b>
                </div>
                {it.external && (
                  <div data-testid={`wa-so-external-${it.key}`}
                    style={{ marginTop: 5, padding: "3px 9px", borderRadius: 6, display: "inline-block",
                             background: "rgba(29,78,216,0.10)", color: "#1D4ED8", fontSize: 11, fontWeight: 700 }}>
                    📍 يُضبط حاليًا من: {it.screen} — خارج لوحة واتساب (عرض هنا فقط، لا يُكرَّر)
                  </div>
                )}
                {it.governs && (
                  <div style={{ fontSize: 11, color: "#57534E", marginTop: 4 }}>⚙️ يحكم: {it.governs}</div>
                )}
                <div style={{ fontSize: 12, marginTop: 5, direction: "ltr", textAlign: "start", fontFamily: "monospace",
                               background: "#F7F2EC", borderRadius: 6, padding: "6px 8px", wordBreak: "break-all" }}>
                  {it.meta_status_only ? (
                    <span data-testid="wa-so-meta-status">
                      {Object.entries(it.value || {}).map(([k, v]) => `${k}: ${v ? "مضبوط ✅" : "غير مضبوط ❌"}`).join("  ·  ")}
                    </span>
                  ) : fmtVal(it.value)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
